import { Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useLumisial } from '../context/LumisialContext';
import { useAuth } from '../context/AuthContext';
import { puedeOperarDineroDeLumisial, shouldShowContextSelector } from '../utils/permissions';
import { LumisialSelector } from '../components/LumisialSelector';
import { Layout } from '../components/Layout';
import { HomePage } from '../pages/HomePage';
import { MovimientosPage } from '../pages/MovimientosPage';
import { DetalleMiembrosPage } from '../pages/DetalleMiembrosPage';
import { DetalleAportesPage } from '../pages/DetalleAportesPage';
import { CampanasPage } from '../pages/CampanasPage';
import { DetalleCampanaPage } from '../pages/DetalleCampanaPage';
import { ActividadesPage } from '../pages/ActividadesPage';
import { DetalleActividadPage } from '../pages/DetalleActividadPage';
import { BalancePage } from '../pages/BalancePage';

function CampanasRoute() {
  const navigate = useNavigate();
  return <CampanasPage onViewDetalle={(id) => navigate(`/lumisial/campanas/${id}`)} />;
}

function DetalleCampanaRoute() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  if (!params.id) {
    return <Navigate to="/lumisial/campanas" replace />;
  }

  return (
    <DetalleCampanaPage
      campañaId={params.id}
      onBack={() => navigate('/lumisial/campanas')}
    />
  );
}

/**
 * Layout route sin `path`: no consume segmento de la URL, así que las rutas hijas
 * siguen matcheando relativas a `/lumisial/` exactamente como antes. Existe para que
 * `cambiar` pueda ser hermana de estas rutas y quedar fuera de `Layout` (sin menú
 * lateral) — spec, scope punto 9.
 */
function LumisialLayoutRoute() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function ActividadesRoute() {
  const navigate = useNavigate();
  return <ActividadesPage onViewDetalle={(id) => navigate(`/lumisial/actividades/${id}`)} />;
}

function DetalleActividadRoute() {
  const navigate = useNavigate();
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  if (!params.id) {
    return <Navigate to="/lumisial/actividades" replace />;
  }

  const tab = searchParams.get('tab');

  
  return (
    <DetalleActividadPage
      actividadId={params.id}
      onBack={() => navigate('/lumisial/actividades')}
      initialTab={tab || undefined}
      readOnly={!puedeOperarDineroDeLumisial(usuario, selectedLumisial)}
    />
  );
}

/**
 * Redirect que preserva los parámetros de la dirección. `<Navigate to="/x" replace />` los
 * descarta, lo que perdería el período en un enlace como `/lumisial/aportes?mes=3&anio=2026`.
 * `replace` (no push) evita que el botón atrás rebote entre la ruta vieja y la nueva.
 */
function RedirectConservandoQuery({ to }: { readonly to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function LumisialRoutes() {
  const { selectedLumisial, setSelectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  const canAccessMultiple = shouldShowContextSelector(usuario);

  useEffect(() => {
    // Quien no puede elegir lumisial siempre opera sobre el suyo: corregir también
    // cuando hay uno distinto seleccionado, no solo cuando no hay ninguno. Un lumisial
    // ajeno pegado hace que la base rechace cualquier escritura por permisos.
    if (canAccessMultiple || !usuario?.lumisial) return;
    if (selectedLumisial?.id !== usuario.lumisial.id) {
      setSelectedLumisial(usuario.lumisial);
    }
  }, [selectedLumisial, canAccessMultiple, usuario, setSelectedLumisial]);

  // Sin lumisial y sin poder elegir uno: la corrección de arriba ya está en camino de
  // fijar el suyo (o hay un problema de datos que no compete arreglar acá). No hay
  // ninguna ruta que mostrar mientras tanto — ni "cambiar", que a este usuario no le
  // corresponde ver bajo ninguna URL.
  if (!selectedLumisial && !canAccessMultiple) {
    return null;
  }

  // Quien no puede elegir lumisial no debe alcanzar el selector ni escribiendo la URL
  // a mano: si ya tiene uno fijo, "cambiar" lo devuelve a su home.
  const rutaCambiar = canAccessMultiple
    ? <LumisialSelector />
    : <Navigate to="/lumisial/home" replace />;

  return (
    <Routes>
      <Route path="cambiar" element={rutaCambiar} />
      {selectedLumisial ? (
        <Route element={<LumisialLayoutRoute />}>
          <Route path="home" element={<HomePage />} />
          <Route path="miembros" element={<DetalleMiembrosPage />} />
          <Route path="campanas" element={<CampanasRoute />} />
          <Route path="campanas/:id" element={<DetalleCampanaRoute />} />
          <Route path="actividades" element={<ActividadesRoute />} />
          <Route path="actividades/:id" element={<DetalleActividadRoute />} />
          <Route path="balance" element={<BalancePage />} />
          <Route path="balance/aportes" element={<DetalleAportesPage />} />
          <Route path="balance/movimientos" element={<MovimientosPage />} />
          {/* Rutas históricas: Aportes y Movimientos vivían en el primer nivel del menú hasta que
              Balance pasó a ser su página madre. Se conservan como redirect permanente por los
              enlaces guardados y por la app instalada, que reabre en la última ruta visitada. */}
          <Route path="aportes" element={<RedirectConservandoQuery to="/lumisial/balance/aportes" />} />
          <Route path="movimientos" element={<RedirectConservandoQuery to="/lumisial/balance/movimientos" />} />
          <Route path="*" element={<Navigate to="/lumisial/home" replace />} />
        </Route>
      ) : (
        // Puede elegir lumisial pero todavía no tiene uno activo (sesión nueva): el
        // único destino posible bajo /lumisial/* es el selector.
        <Route path="*" element={<Navigate to="/lumisial/cambiar" replace />} />
      )}
    </Routes>
  );
}
